import { LegendLayerAttributeConditionDc, LegendLayerAttributeDc, LegendLayerDc, GeometryType } from "@evergis/api";
import { convertSvgToBase64, manipulateSvgSymbol, SvgPointSymbol } from "@evergis/react";
import { RangeValue } from "@evergis/ui";
import { SVG } from "@svgdotjs/svg.js";

import { createLegendSections, LegendSectionItem, LegendSectionProps } from "../legend/createLegend";
import { getLegendSymbolSize } from "../legend/getLegendSymbolSize";
import { isSvgPointSymbol, StyleSymbolType } from "../../../interfaces/style";
import { compoundIconsMap } from "../../../modules/LayerList/components/CompoundIcon";
import { getSymbolImage } from "../../../react-sgis/symbols/helpers";
import { getByteStringFromDataUri, svgToPng } from "../../../utils/image";
import { promiseAllSettled } from "../../../utils/promiseAllSettled";
import { i18n } from "../../../i18n";
import { EditGeometryType } from "../attributes/geometryTypes";
import { LayerInfo } from "../../../interfaces/resourceInfo";
import { getParameterValue } from "../classification/getFromClassified";

const PRINT_SYMBOL_SIZE = 24;

export async function createLegendLayers(layers: LayerInfo[], defaultScaleRange: RangeValue): Promise<LegendLayerDc[]> {
  try {
    return await promiseAllSettled(layers.map(layer => createLegendLayer(layer, defaultScaleRange)));
  } catch (error) {
    // ignore error
    return null;
  }
}

const getEmptyLayer = (layerName: string) => {
  console.warn(`print: unable to create styled legend for ${layerName}`);

  return { name: layerName };
};

async function createLegendLayer(layer: LayerInfo, defaultScaleRange: RangeValue): Promise<LegendLayerDc> {
  const layerName = getLayerDisplayName(layer);
  const { geometryType, style, layerDefinition, name } = layer;

  if (!layerDefinition || !style) {
    return getEmptyLayer(layerName);
  }

  const { classificationManager, symbol } = style;
  const { attributes } = layerDefinition;

  if (!classificationManager || !symbol || !attributes) {
    return getEmptyLayer(layerName);
  }

  const legend = createLegendSections(style, attributes, defaultScaleRange);
  const layerSymbolImageData = await getLayerSymbolImage(
    symbol,
    geometryType,
    style.classificationManager.hasClassifications(),
  );

  return {
    name: layerName,
    symbol: { value: getByteStringFromDataUri(layerSymbolImageData) },
    attributes: await promiseAllSettled(legend.map(createLegendAttributes)),
    layerServiceName: name,
  };
}

async function createLegendAttributes(legendItem: LegendSectionProps): Promise<LegendLayerAttributeDc> {
  return {
    name: legendItem.sectionTitle,
    conditions: await promiseAllSettled(legendItem.items.map(createAttributeCondition)),
  };
}

async function createAttributeCondition(
  attributeCondition: LegendSectionItem,
): Promise<LegendLayerAttributeConditionDc> {
  const { symbol, title } = attributeCondition;

  const symbolImage = await getSymbolImage(symbol, getLegendSymbolSize(symbol), true);
  const symbolValue = getByteStringFromDataUri(symbolImage);

  return {
    value: title || i18n.t("classification.other", { ns: "common" }),
    symbol: { value: symbolValue },
  };
}

function getLayerDisplayName(layer: LayerInfo): string {
  return layer && (layer.alias || layer.name);
}

async function getLayerSymbolImage(
  symbol: StyleSymbolType,
  geometryType: GeometryType | EditGeometryType,
  hasClassification?: boolean,
): Promise<string> {
  let symbolImage;

  if (isSvgPointSymbol(symbol)) {
    const svg = manipulateSvgSymbol({ SVGContainer: SVG(), symbol: symbol as SvgPointSymbol });

    symbolImage = await svgToPng(convertSvgToBase64(svg.svg()), getParameterValue(symbol.size));

    return symbolImage;
  }

  symbolImage = hasClassification
    ? await svgToPng(compoundIconsMap[geometryType], PRINT_SYMBOL_SIZE)
    : await getSymbolImage(symbol, getLegendSymbolSize(symbol));

  return symbolImage;
}
